import * as React from "react";
import {OTPInput, OTPInputContext} from "input-otp";
import type {RenderProps} from "input-otp";

import {cn} from "@/utils/styles";

const InputOTP = React.forwardRef<
  React.ComponentRef<typeof OTPInput>,
  React.ComponentPropsWithoutRef<typeof OTPInput>
>(({className, containerClassName, ...props}, ref) => (
  <OTPInput
    ref={ref}
    containerClassName={cn(
      "flex items-center justify-center gap-g-1 has-[:disabled]:opacity-50",
      containerClassName,
    )}
    className={cn("disabled:cursor-not-allowed", className)}
    {...props}
  />
));
InputOTP.displayName = "InputOTP";

const InputOTPGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(({className, ...props}, ref) => (
  <div
    ref={ref}
    className={cn("gap-g-1 flex items-center justify-center", className)}
    {...props}
  />
));
InputOTPGroup.displayName = "InputOTPGroup";

const InputOTPSlot = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div"> & {index: number}
>(({index, className, ...props}, ref) => {
  const inputOTPContext = React.useContext<RenderProps>(OTPInputContext);
  const {char, hasFakeCaret, isActive} = inputOTPContext.slots[index];

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex size-12 items-center justify-center rounded-md border text-2xl font-semibold transition-all",
        "border-outline bg-outline-variant text-on-surface",
        isActive &&
          "border-primary ring-primary/80 ring-offset-background z-10 ring-2",
        className,
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="bg-primary h-5 w-px animate-pulse" />
        </div>
      )}
    </div>
  );
});
InputOTPSlot.displayName = "InputOTPSlot";

const InputOTPSeparator = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(({className, ...props}, ref) => (
  <div
    ref={ref}
    role="separator"
    className={cn("mx-g-1 flex items-center justify-center", className)}
    {...props}
  >
    <span className="bg-outline size-1 rounded-full" />
  </div>
));
InputOTPSeparator.displayName = "InputOTPSeparator";

export {InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator};
